import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "customUrls", "customThemes", "theme", "isTransparent", "isBlurred", "position", "countryPrefix", "initialSearchTerm", "layoutConfig", "amplitudePageName", "topBannerIsOpen", "organizationSlug", "centralizeOrganizationId"];
import { createContext, useDeferredValue, useEffect, useLayoutEffect } from "react";
import cn from "classnames";
import { useActiveSubmenu, useMainMenuTheme, useMainMenuUrls, useMediaQuery, useSearchTerm, useUserWithRoleAndPermissions, useVisibleHeight } from "./hooks";
import { jsx as _jsx } from "react/jsx-runtime";
export var MainMenuContext = /*#__PURE__*/createContext(undefined);
export var MainMenuProvider = function MainMenuProvider(_ref) {
  var children = _ref.children,
    customUrls = _ref.customUrls,
    customThemes = _ref.customThemes,
    themeProp = _ref.theme,
    isTransparent = _ref.isTransparent,
    isBlurred = _ref.isBlurred,
    _ref$position = _ref.position,
    position = _ref$position === void 0 ? 'relative' : _ref$position,
    countryPrefix = _ref.countryPrefix,
    initialSearchTerm = _ref.initialSearchTerm,
    layoutConfig = _ref.layoutConfig,
    amplitudePageName = _ref.amplitudePageName,
    topBannerIsOpen = _ref.topBannerIsOpen,
    organizationSlug = _ref.organizationSlug,
    centralizeOrganizationId = _ref.centralizeOrganizationId,
    mainMenuProps = _objectWithoutProperties(_ref, _excluded);
  var _useMediaQuery = useMediaQuery('sm'),
    matchesSm = _useMediaQuery.matches;
  var _useMediaQuery2 = useMediaQuery('md'),
    matchesMd = _useMediaQuery2.matches;
  var _useMediaQuery3 = useMediaQuery('lg'),
    matchesLg = _useMediaQuery3.matches;
  var deferredMatchesSm = useDeferredValue(matchesSm);
  var currentBreakpointKey = 'xs';
  if (matchesSm) {
    currentBreakpointKey = 'sm';
  }
  if (matchesMd) {
    currentBreakpointKey = 'md';
  }
  if (matchesLg) {
    currentBreakpointKey = 'lg';
  }
  var _useUserWithRoleAndPe = useUserWithRoleAndPermissions(),
    me = _useUserWithRoleAndPe.me,
    loading = _useUserWithRoleAndPe.loading;
  var searchTermProps = useSearchTerm({
    initialSearchTerm: initialSearchTerm
  });
  var urls = useMainMenuUrls({
    customUrls: customUrls,
    me: me,
    countryPrefix: countryPrefix,
    amplitudePageName: amplitudePageName
  });
  var activeSubmenuProps = useActiveSubmenu({
    amplitudePageName: amplitudePageName,
    onLeftMenuClick: mainMenuProps.onLeftMenuClick,
    onChangeActiveSubmenu: mainMenuProps.onChangeActiveSubmenu
  });
  var searchOverlayIsOpen = activeSubmenuProps.activeSubMenu === 'search' && (currentBreakpointKey === 'md' || currentBreakpointKey === 'lg');
  var _useMainMenuTheme = useMainMenuTheme({
      customThemes: customThemes,
      theme: themeProp,
      isTransparent: isTransparent,
      searchOverlayIsOpen: searchOverlayIsOpen
    }),
    theme = _useMainMenuTheme.theme,
    themeName = _useMainMenuTheme.themeName;
  var mainMenuVisibilityProps = useVisibleHeight({
    position: position,
    theme: theme,
    topBannerIsOpen: topBannerIsOpen
  });
  useEffect(function () {
    /*
     * We need to override the body background on open
     * to fill the empty space created when the scroll is removed
     * (ignore this behaviour when the navbar is transparent)
     */
    if (!isTransparent) {
      if (activeSubmenuProps.activeSubMenu) {
        document.body.className = cn(document.body.className, theme.nav.backgroundColorClassName);
      } else {
        document.body.className = document.body.className.replace(theme.nav.backgroundColorClassName, '');
      }
    }
  }, [activeSubmenuProps.activeSubMenu, theme.nav.backgroundColorClassName, isTransparent]);
  useLayoutEffect(function () {
    var hasActiveSubmenuOpen = !!activeSubmenuProps.activeSubMenu;

    // Note: This is a workaround to close the submenu when the breakpoint changes.
    // It's needed because the submenu is not rendered when the breakpoint changes.
    if (hasActiveSubmenuOpen && deferredMatchesSm && !matchesSm) {
      activeSubmenuProps.onCloseSubmenu();
    }
  }, [activeSubmenuProps, activeSubmenuProps.activeSubMenu, deferredMatchesSm, matchesSm]);
  return /*#__PURE__*/_jsx(MainMenuContext.Provider, {
    value: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, mainMenuProps), {}, {
      loadingMe: loading,
      me: me,
      urls: _objectSpread(_objectSpread({}, urls), {}, {
        searchLanding: "".concat(urls.searchLanding, "?q=").concat(searchTermProps.searchTerm.cleanValue)
      }),
      theme: theme,
      themeName: themeName,
      isTransparent: isTransparent,
      isBlurred: isBlurred,
      currentBreakpointKey: currentBreakpointKey,
      position: position,
      countryPrefix: countryPrefix,
      amplitudePageName: amplitudePageName,
      layoutConfig: _objectSpread({
        // TODO: Enable visibility when the feature is ready
        hideInterestsButton: true,
        // It should be activated in every page that supports both dark and light modes.
        hideNightSwitch: true,
        // MicroDegrees are not available for Brazil
        hideMicroDegrees: countryPrefix === 'br' || (layoutConfig === null || layoutConfig === void 0 ? void 0 : layoutConfig.hideMicroDegrees),
        // Should be explicitly enabled
        hideHigherEdLink: true,
        phoneInputProps: {
          hide: true,
          source: ''
        },
        hideMentorLandingLink: !!(me !== null && me !== void 0 && me.isPremium),
        // Should be explicitly enabled
        hideDownloadableResouces: true,
        // Should be enabled only on blog pages
        hideBlogCategories: true,
        // Should be explicitly enabled
        hideSocialLinks: true,
        // Should be explicitly enabled
        hideBlogSearch: true,
        // Should be explicitly enabled
        hideDesktopBlogLeftMenu: true,
        // Should be explicitly enabled
        hideSolutionsMenu: true,
        // Should be explicitly enabled
        hideCrehanaCoinsButton: true
      }, layoutConfig),
      organizationSlug: organizationSlug,
      centralizeOrganizationId: centralizeOrganizationId,
      initialSearchTerm: initialSearchTerm
    }, mainMenuVisibilityProps), searchTermProps), activeSubmenuProps),
    children: children
  });
};